import React, { useState, useEffect } from "react";
import { Inventory2, LocalShipping, Description, CheckCircle, AllInbox, DirectionsBike } from "@mui/icons-material";
import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	CircularProgress,
	Skeleton,
} from "@mui/material";
import { toast } from "react-toastify";

import { ApiService } from "../../ApiService";

export const VisitasEnvioEdit = ({ companyData, currentBranch }) => {
	// Loading states
	const [isLoading, setIsLoading] = useState(false);
	const [isDataLoading, setIsDataLoading] = useState(true);

	// Icons array for the hexagons
	const icons = [
		<Inventory2 fontSize="large" style={{ color: "red", fontSize: "28px" }} />,
		<AllInbox fontSize="large" style={{ color: "rgb(33, 182, 33)", fontSize: "28px" }} />,
		<Description fontSize="large" style={{ color: "#187064", fontSize: "28px" }} />,
		<LocalShipping fontSize="large" style={{ color: "#435eb9", fontSize: "28px" }} />,
		<DirectionsBike fontSize="large" style={{ color: "#ff7101", fontSize: "28px" }} />,
		<CheckCircle fontSize="large" style={{ color: "#e450ca", fontSize: "28px" }} />,
	];

	// State for hexagon data
	const [hexData, setHexData] = useState([
		{
			icon: icons[0],
			title: "",
			titleOriginal: "",
			text: "",
			textOriginal: "",
			enabled: false,
		},
		{
			icon: icons[1],
			title: "",
			titleOriginal: "",
			text: "",
			textOriginal: "",
			enabled: false,
		},
		{
			icon: icons[2],
			title: "",
			titleOriginal: "",
			text: "",
			textOriginal: "",
			enabled: false,
		},
		{
			icon: icons[3],
			title: "",
			titleOriginal: "",
			text: "",
			textOriginal: "",
			enabled: false,
		},
		{
			icon: icons[4],
			title: "",
			titleOriginal: "",
			text: "",
			textOriginal: "",
			enabled: false,
		},
		{
			icon: icons[5],
			title: "",
			titleOriginal: "",
			text: "",
			textOriginal: "",
			enabled: false,
		},
	]);

	// State for modal
	const [editIndex, setEditIndex] = useState(null);
	const [tempTitle, setTempTitle] = useState("");
	const [tempText, setTempText] = useState("");

	// Character limits
	const TITLE_MAX = 35;
	const TEXT_MAX = 135;

	// Load data from companyData when it changes
	useEffect(() => {
		if (companyData && companyData.view_sendP) {
			setIsDataLoading(true);

			// Create a copy of hexData to update
			const updatedHexData = [...hexData];

			// Update each hex item with data from companyData
			companyData.view_sendP.forEach((item, index) => {
				if (index < updatedHexData.length) {
					updatedHexData[index].title = item.title || "";
					updatedHexData[index].titleOriginal = item.title || "";
					updatedHexData[index].text = item.text || "";
					updatedHexData[index].textOriginal = item.text || "";
					updatedHexData[index].enabled = item.enabled || false;
				}
			});

			setHexData(updatedHexData);
			setIsDataLoading(false);
		}
	}, [companyData]);

	// Modal functions
	const handleOpenModal = (index) => {
		setEditIndex(index);
		setTempTitle(hexData[index].title);
		setTempText(hexData[index].text);
	};

	const handleCloseModal = () => {
		setEditIndex(null);
	};

	// Toggle enabled state
	const handleToggleEnabled = async (index) => {
		if (!currentBranch) return;

		setIsLoading(true);

		// Create updated data for API - array with empty objects except for the toggled one
		const updatedData = {
			view_sendP: hexData.map((_, i) => {
				if (i === index) {
					return {
						title: hexData[index].titleOriginal,
						text: hexData[index].textOriginal,
						enabled: !hexData[index].enabled, // Toggle the enabled state
					};
				}
				return {}; // Empty object for all other positions
			}),
		};

		try {
			const url = `api/company/update/us?key_branch=${currentBranch}`;
			const payload = {
				method: "PUT",
				body: JSON.stringify(updatedData),
			};

			const response = await ApiService.fetchData(url, payload);

			if (response?.code === 200) {
				// Update local state with new enabled value
				setHexData((prev) => {
					const newData = [...prev];
					newData[index].enabled = !newData[index].enabled;
					return newData;
				});

				toast.success(
					response.message || `Hexágono ${hexData[index].enabled ? "desactivado" : "activado"} correctamente.`
				);
			} else {
				toast.error(response?.message || "Error al actualizar el estado del hexágono.");
			}
		} catch (error) {
			console.error("Error toggling enabled state:", error);
			toast.error("Ocurrió un error al cambiar el estado del hexágono.");
		} finally {
			setIsLoading(false);
		}
	};

	// Handle input changes
	const handleTitleChange = (e) => {
		const newValue = e.target.value;
		// Limit to TITLE_MAX characters
		if (newValue.length <= TITLE_MAX) {
			setTempTitle(newValue);
		}
	};

	const handleTextChange = (e) => {
		const newValue = e.target.value;
		// Limit to TEXT_MAX characters
		if (newValue.length <= TEXT_MAX) {
			setTempText(newValue);
		}
	};

	// Save changes to API
	const handleSaveChanges = async () => {
		// Validation
		if (!tempTitle.trim()) {
			toast.error("El título no puede quedar vacío.");
			return;
		}

		if (!tempText.trim()) {
			toast.error("La descripción no puede quedar vacía.");
			return;
		}

		if (tempTitle.length > TITLE_MAX) {
			toast.error(`El título excede ${TITLE_MAX} caracteres.`);
			return;
		}

		if (tempText.length > TEXT_MAX) {
			toast.error(`La descripción excede ${TEXT_MAX} caracteres.`);
			return;
		}

		// If everything is valid, proceed with API update
		if (!currentBranch) return;

		setIsLoading(true);

		// Create updated data for API - array with empty objects except for the edited one
		const updatedData = {
			view_sendP: hexData.map((_, i) => {
				if (i === editIndex) {
					return {
						title: tempTitle.trim(),
						text: tempText.trim(),
						enabled: hexData[editIndex].enabled,
					};
				}
				return {}; // Empty object for all other positions
			}),
		};

		try {
			const url = `api/company/update/us?key_branch=${currentBranch}`;
			const payload = {
				method: "PUT",
				body: JSON.stringify(updatedData),
			};

			const response = await ApiService.fetchData(url, payload);

			if (response?.code === 200) {
				// Update local state with new values
				setHexData((prev) => {
					const newData = [...prev];
					newData[editIndex].title = tempTitle.trim();
					newData[editIndex].titleOriginal = tempTitle.trim();
					newData[editIndex].text = tempText.trim();
					newData[editIndex].textOriginal = tempText.trim();
					return newData;
				});

				toast.success(response.message || "Datos actualizados correctamente.");
				handleCloseModal();
			} else {
				toast.error(response?.message || "Error al actualizar los datos.");
			}
		} catch (error) {
			console.error("Error updating hex data:", error);
			toast.error("Ocurrió un error al guardar los cambios.");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div>
			<div className="envios-company">
				{/* TÍTULO PRINCIPAL (NO EDITABLE) */}
				<div className="title-envios">
					<h2>SISTEMA DE ENVIO DE NUESTROS PRODUCTOS</h2>
				</div>

				{/* Primera fila de hexágonos */}
				{isDataLoading ? (
					<>
						<div className="content-hex-top">
							{[0, 1, 2].map((index) => (
								<div className="hexagon" key={index}>
									<Skeleton variant="rectangular" width="100%" height={150} />
								</div>
							))}
						</div>
						<div className="content-hex-bottom">
							{[3, 4, 5].map((index) => (
								<div className="hexagon" key={index}>
									<Skeleton variant="rectangular" width="100%" height={150} />
								</div>
							))}
						</div>
					</>
				) : (
					<>
						<div className="content-hex-top">
							{hexData.slice(0, 3).map((item, index) => (
								<div
									className="hexagon"
									key={index}
									onClick={() => handleOpenModal(index)}
									style={{ cursor: "pointer" }}>
									<div className="hex-content">
										{item.icon}
										<h3>{item.title}</h3>
										<p>{item.text}</p>
										<div
											style={{
												position: "absolute",
												top: "5px",
												right: "5px",
												width: "10px",
												height: "10px",
												borderRadius: "50%",
												backgroundColor: item.enabled ? "green" : "red",
											}}
											title={item.enabled ? "Activo" : "Inactivo"}
										/>
									</div>
								</div>
							))}
						</div>

						{/* Segunda fila de hexágonos */}
						<div className="content-hex-bottom">
							{hexData.slice(3, 6).map((item, idx) => {
								const actualIndex = 3 + idx; // 3,4,5
								return (
									<div
										className="hexagon"
										key={actualIndex}
										onClick={() => handleOpenModal(actualIndex)}
										style={{ cursor: "pointer" }}>
										<div className="hex-content">
											{item.icon}
											<h3>{item.title}</h3>
											<p>{item.text}</p>
											<div
												style={{
													position: "absolute",
													top: "5px",
													right: "5px",
													width: "10px",
													height: "10px",
													borderRadius: "50%",
													backgroundColor: item.enabled ? "green" : "red",
												}}
												title={item.enabled ? "Activo" : "Inactivo"}
											/>
										</div>
									</div>
								);
							})}
						</div>
					</>
				)}
			</div>

			{/* MODAL DE EDICIÓN */}
			<Dialog open={editIndex !== null} onClose={handleCloseModal}>
				<DialogTitle>Editar Hexágono</DialogTitle>
				<DialogContent dividers>
					<div style={{ display: "flex", flexDirection: "column", gap: "16px", minWidth: "100%" }}>
						<TextField
							label={`Titulo: (${tempTitle.length} / ${TITLE_MAX} caracteres)`}
							variant="outlined"
							value={tempTitle}
							slotProps={{
								htmlInput: {
									maxLength: 35,
								},
							}}
							onChange={handleTitleChange}
							disabled={isLoading}
						/>
						<TextField
							label={`Descripción: (${tempText.length} / ${TEXT_MAX} caracteres)`}
							variant="outlined"
							multiline
							minRows={3}
							value={tempText}
							onChange={handleTextChange}
							disabled={isLoading}
						/>
					</div>
				</DialogContent>
				<DialogActions>
					<Button
						color={hexData[editIndex]?.enabled ? "error" : "success"}
						variant="outlined"
						onClick={() => handleToggleEnabled(editIndex)}
						disabled={isLoading}
						sx={{ marginRight: 1 }}>
						{hexData[editIndex]?.enabled ? "Desactivar" : "Activar"}
					</Button>
					<Button onClick={handleCloseModal} disabled={isLoading}>
						Cancelar
					</Button>
					<Button
						variant="contained"
						onClick={handleSaveChanges}
						disabled={isLoading}
						startIcon={isLoading ? <CircularProgress size={20} /> : null}>
						{isLoading ? "Guardando..." : "Guardar"}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};
